import { NetworkClient } from './platform-interfaces/network-client'
import { Storage } from './platform-interfaces/storage'
import { BaseEvent, FlushPriority } from '@mmt/events-schema'
import { AnalyticsConfig } from './analytics-sdk'
import { getUniqueId } from './global-state/id-generators'

type JobStatus = 'pending' | 'running' | 'failed' | 'done'
type Job<T> = {
  id: string
  events: Array<BaseEvent<T>>
  status: JobStatus
  start?: number
  retryCount: number
}

const JOB_TIMEOUT = 60 * 1000
export interface IJobQueue<T> {
  start(): void
  shutdown(): void
  flushNow(priority: FlushPriority): void
  pushEvent(event: BaseEvent<T>): void
}
export class JobQueue<T> implements IJobQueue<T> {
  private readonly networkClient: NetworkClient<T>
  private readonly storage: Storage
  private readonly config: AnalyticsConfig
  private jobs: Array<Job<T>> = []
  private _timer: number = -1

  constructor(networkClient: NetworkClient<T>, storage: Storage, config: AnalyticsConfig) {
    this.networkClient = networkClient
    this.storage = storage
    this.config = config
    this.restoreState()
  }

  public start() {
    if (this._timer === -1) {
      this._timer = setInterval(() => this.execute(), this.config.autoFlushIntervalInMs)
    }
  }

  public shutdown() {
    clearInterval(this._timer)
    this._timer = -1
  }

  public flushNow(priority: FlushPriority = 'normal') {
    this.execute(priority)
  }

  private saveState() {
    this.storage.setItem('@events/job-queue', JSON.stringify(this.jobs))
  }

  private restoreState() {
    const item = this.storage.getItem('@events/job-queue')
    if (item) {
      this.jobs = JSON.parse(item)
      this.jobs = this.jobs.map<Job<T>>(job => ({ ...job, status: 'pending' }))
    }
  }

  static findLast<T>(arr: Array<T>, predicate: (arg: T, index: number) => boolean): T | undefined {
    for (let i = arr.length - 1; i >= 0; i--) {
      const item = arr[i]
      if (predicate(item, i)) {
        return item
      }
    }
    return undefined
  }
  public pushEvent(event: BaseEvent<T>) {
    let pendingJob = JobQueue.findLast(
      this.jobs,
      value => value.status === 'pending' && value.events.length < this.config.maxBatchSize,
    )
    if (!pendingJob || pendingJob.events.length >= this.config.maxBatchSize) {
      pendingJob = {
        events: [],
        status: 'pending',
        id: getUniqueId(),
        retryCount: 0,
      }
      this.jobs.push(pendingJob)
    }
    pendingJob.events.push(event)
    this.saveState()
  }

  private execute(prioity: FlushPriority = 'normal') {
    const pendingJobs = this.jobs.filter(job => job.status === 'pending')
    for (const job of pendingJobs) {
      this.updateJobStatus(job, 'running')
      this.networkClient.sendEvents(job.events, prioity, null).then(jobResult => {
        if (jobResult === 'success') {
          this.updateJobStatus(job, 'done')
        } else if (jobResult === 'failed') {
          this.updateJobStatus(job, 'failed')
        }
      })
    }
  }

  private updateJobStatus(job: Job<T>, newStatus: JobStatus) {
    let updatedJobs = this.jobs.map<Job<T>>((value: Job<T>) => {
      if (value.id !== job.id) {
        return value
      }
      switch (newStatus) {
        case 'done':
          return {
            ...value,
            status: 'done',
          }
        case 'running':
          return {
            ...value,
            start: Date.now(),
            status: 'running',
          }
        case 'failed':
          return {
            ...value,
            status: value.retryCount < 2 ? 'pending' : 'done',
            retryCount: value.retryCount + 1,
          }
        default:
          return value
      }
    })

    updatedJobs = updatedJobs
      // prune done tasks
      .filter(job => job.status !== 'done')
      // reset long running task
      .filter(job => !(job.status === 'running' && Date.now() - (job.start || 0) > JOB_TIMEOUT))

    this.jobs = updatedJobs

    this.saveState()
  }
}
